import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/landing/Reveal";
import { editions } from "@/lib/landingData";
import { ArrowRight } from "lucide-react";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const tabKeys = ["student", "kids"];

const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      data-testid={`edition-feature-${index}`}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0C0C0E] p-7 transition-colors hover:border-white/25"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full glow-orange opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
      <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-brand text-black transition-transform duration-300 group-hover:scale-110">
        <Icon className="h-6 w-6" />
      </div>
      <h4 className="font-display text-lg font-bold uppercase tracking-tight text-white">
        {feature.title}
      </h4>
      <p className="mt-1.5 text-sm font-semibold text-gradient">{feature.tagline}</p>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{feature.copy}</p>
    </motion.div>
  );
};

export const Editions = () => {
  const [active, setActive] = useState("student");
  const ed = editions[active];
  const TabIcon = ed.icon;

  return (
    <section data-testid="editions" id="editions" className="relative py-28">
      <div className="pointer-events-none absolute left-1/2 top-40 -z-10 h-[380px] w-[600px] max-w-full -translate-x-1/2 glow-violet blur-3xl opacity-40" />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gradient">
            Made For Every Stage of Life
          </p>
          <h2 className="mt-5 font-mega text-4xl leading-[0.95] text-white sm:text-5xl lg:text-6xl">
            One Coach. Every Chapter.
          </h2>
        </Reveal>

        {/* Segmented toggle */}
        <div className="mt-10 flex justify-center">
          <div className="relative inline-flex rounded-full border border-white/10 bg-[#0C0C0E] p-1.5">
            {tabKeys.map((key) => {
              const isActive = active === key;
              return (
                <button
                  key={key}
                  onClick={() => setActive(key)}
                  data-testid={`edition-tab-${key}`}
                  className="relative z-10 rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors sm:px-8"
                >
                  {isActive && (
                    <motion.span
                      layoutId="edition-pill"
                      className="absolute inset-0 -z-10 rounded-full bg-gradient-brand"
                      transition={{ type: "spring", stiffness: 420, damping: 34 }}
                    />
                  )}
                  <span className={isActive ? "text-black" : "text-white/60"}>
                    {editions[key].tab}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Intro */}
            <div className="mx-auto mt-16 max-w-3xl text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-brand text-black">
                <TabIcon className="h-7 w-7" />
              </span>
              <h3 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                {ed.headline}
              </h3>
              <p className="mx-auto mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                {ed.intro}
              </p>
              <button
                onClick={() => scrollTo("start")}
                data-testid="edition-cta-btn"
                className="group mt-8 inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gradient-brand px-8 text-sm font-bold uppercase tracking-wide text-black shadow-[0_14px_50px_-12px_rgba(249,115,22,0.65)] transition-all hover:scale-[1.04]"
              >
                {ed.cta}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Features */}
            <p className="mt-20 text-center font-display text-sm font-bold uppercase tracking-[0.25em] text-white/70">
              {ed.featuresTitle}
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {ed.features.map((f, i) => (
                <FeatureCard key={f.title} feature={f} index={i} />
              ))}
            </div>

            {/* Closing */}
            <div className="relative mt-16 overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0C] p-10 text-center sm:p-14">
              <div className="pointer-events-none absolute left-1/2 h-40 w-96 max-w-full -translate-x-1/2 glow-gold blur-3xl opacity-50" />
              <h3 className="font-mega text-3xl leading-[0.95] text-white sm:text-4xl">
                {ed.closingTitle}
              </h3>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                {ed.closingCopy}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Editions;
